import { type MeDto, type ProcessInboxCountDto, type ProcessTaskDto } from '@bqcrm/shared';
import { Badge, Card, Select, Tabs, Text } from '@mantine/core';
import { IconInbox } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { api } from '../api';
import { can } from '../auth';
import { PageHeader } from '../components/PageHeader';
import { ProcessTaskList } from '../components/process/ProcessTasks';
import { QueryState } from '../components/QueryState';
import { EmptyState } from '../components/ui';
import { useTrades } from '../data';

type View = 'moje' | 'do_przejecia' | 'wszystkie' | 'nieobsadzone';

/** „Moje zadania”: zadania przypisane do mnie (także jako zastępca), do przejęcia z kolejki, a dla przydzielających — wszystkie. */
export function TasksInboxPage({ me }: { me: MeDto }) {
  const [view, setView] = useState<View>('moje');
  const [params] = useSearchParams();
  const q =useQuery({ queryKey: ['process-inbox', view], queryFn: () => api<ProcessTaskDto[]>(`/process/inbox?widok=${view}`), refetchInterval: 60_000 });
  const counts = useQuery({ queryKey: ['process-inbox-count'], queryFn: () => api<ProcessInboxCountDto>('/process/inbox/count'), refetchInterval: 60_000 });
  const admin = can(me, 'obieg.przydzielanie', 'W');
  const trades = useTrades();
  const [trade, setTrade] = useState<string | null>(null);
  const all = q.data ?? [];
  const items = trade ? all.filter((t) => t.tradeCodes.includes(trade)) : all;
  const unstaffed = useQuery({ queryKey: ['process-inbox', 'nieobsadzone'], queryFn: () => api<ProcessTaskDto[]>('/process/inbox?widok=nieobsadzone'), refetchInterval: 60_000, enabled: admin });
  const present = [...new Set(all.flatMap((t) => t.tradeCodes))];

  // zastępstwo ustawia się przy nieobecności w Kalendarzu (docs/16) — dawne linki „?zastepstwa” otwierają nową nieobecność
  if (params.has('zastepstwa')) {
    const p = new URLSearchParams({ nowa: 'nieobecnosc' });
    for (const k of ['kto', 'od', 'do', 'sprawa']) if (params.get(k)) p.set(k, params.get(k)!);
    return <Navigate to={`/kalendarz?${p.toString()}`} replace />;
  }

  return (
    <>
      <PageHeader
        title="Moje zadania"
        description="Zadania w sprawach: przypisane do Ciebie i czekające w kolejce na kogoś z Twoimi uprawnieniami. Zadanie kończy się samo, gdy wykonasz pracę w sprawie (np. wydasz ofertę). Zastępcę na czas nieobecności wskażesz przy nieobecności w Kalendarzu."
      />
      <Tabs value={view} onChange={(v) => v && setView(v as View)} mb="md">
        <Tabs.List>
          <Tabs.Tab value="moje" rightSection={counts.data?.mine ? <Badge size="xs" color={counts.data.overdue ? 'red' : 'eko'}>{counts.data.mine}</Badge> : undefined}>
            Moje
          </Tabs.Tab>
          <Tabs.Tab value="do_przejecia" rightSection={counts.data?.pool ? <Badge size="xs" color="orange">{counts.data.pool}</Badge> : undefined}>
            Do przejęcia
          </Tabs.Tab>
          {admin && <Tabs.Tab value="wszystkie">Wszystkie otwarte</Tabs.Tab>}
          {admin && (
            <Tabs.Tab value="nieobsadzone" rightSection={unstaffed.data?.length ? <Badge size="xs" color="orange">{unstaffed.data.length}</Badge> : undefined}>
              Nieobsadzone
            </Tabs.Tab>
          )}
        </Tabs.List>
      </Tabs>
      {view === 'nieobsadzone' && (
        <Text size="sm" c="dimmed" mb="sm">
          Zadania w kolejce, których nikt nie może przejąć: brak osoby z uprawnieniem, dostępem do firmy i funkcją w branży zadania. Przydziel ręcznie (wyjątek z komentarzem) albo daj komuś rolę z tą kolejką i branżą (Pracownicy → karta pracownika → Dostęp).
        </Text>
      )}
      {present.length > 1 && (
        <Select
          mb="sm"
          w={{ base: '100%', sm: 240 }}
          size="xs"
          placeholder="Wszystkie branże"
          clearable
          data={present.map((c) => ({ value: c, label: trades.data?.find((t) => t.code === c)?.name ?? c }))}
          value={trade}
          onChange={setTrade}
        />
      )}
      <QueryState isLoading={q.isLoading} error={q.error}>
        {items.length ? (
          <ProcessTaskList tasks={items} showCase />
        ) : (
          <Card withBorder>
            <EmptyState
              icon={<IconInbox size={24} />}
              title={view === 'do_przejecia' ? 'Kolejka jest pusta' : view === 'nieobsadzone' ? 'Wszystkie zadania mają kogoś, kto może je przejąć' : 'Nic nie czeka na Ciebie'}
              hint={view === 'moje' ? 'Gdy ktoś przydzieli Ci zadanie albo przejmiesz je z kolejki, pojawi się tutaj i w dzwonku.' : undefined}
            />
          </Card>
        )}
      </QueryState>
    </>
  );
}
